// Client-facing modals: detail view, add/edit form, WhatsApp launcher, and the
// self-fetching payments / notes timelines used by the detail view.
import { useState, useEffect } from "react";
import { doc, setDoc, getDocs, deleteDoc, collection, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { packageDisplayState } from "../domain";
import { fmt, sl, uid, clientPhone, openWhatsApp, sortOtherLast } from "../lib/format";
import { pkgDisplayName } from "../lib/packages";
import { themeTokens } from "../lib/theme";
import { Modal, Btn, Inp, SD, ConfirmButton, Icon, useToast, useFormGuard } from "../ui/primitives";
import { PhoneField, PkgBuilder, SchedBuilder } from "../ui/forms";
import { CC, COUNTRIES } from "../data/countries";
import { CLIENT_PROFESSIONS, CLIENT_SECTORS } from "../data/lists";

export const WhatsAppButton=({client,theme,profile})=>{
  const sLabel=sl(profile,1).toLowerCase();
  const sLabels=sl(profile,2).toLowerCase();
  const [open,setOpen]=useState(false);
  const templates=[
    {label:"Session Reminder",msg:`Hi ${client.name}! 👋 Just a reminder that your ${sLabel} is scheduled. See you soon!`},
    {label:"Package Expiring",msg:`Hi ${client.name}! Your package is almost over — only ${client.classesRemaining||0} ${sLabels} remaining. Renew today to keep going! 💪`},
    {label:"Payment Due",msg:`Hi ${client.name}! This is a gentle reminder that your payment is pending. Please clear it at your earliest convenience. Thank you!`},
    {label:"Welcome",msg:`Hi ${client.name}! Welcome to the team! 🎉 We're excited to have you. Let's achieve great things together!`},
    {label:"Custom",msg:""},
  ];
  const [sel,setSel]=useState(0);
  const [custom,setCustom]=useState("");
  const send=()=>{
    const msg=sel===4?custom:templates[sel].msg;
    if(!msg.trim())return;
    openWhatsApp(clientPhone(client),msg);
    setOpen(false);
  };
  if(!clientPhone(client))return null;
  return(
    <>
      <button onClick={()=>setOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
        style={{background:"#dcfce7",color:"#16a34a"}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="#16a34a"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </button>
      {open&&(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-4" onClick={()=>setOpen(false)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">WhatsApp Message</h3>
              <button onClick={()=>setOpen(false)} className="text-gray-400 hover:text-gray-600"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>
            <p className="text-sm text-gray-500 mb-3">To: <strong>{client.name}</strong> ({client.phone})</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {templates.map((t,i)=>(
                <button key={i} onClick={()=>setSel(i)} className="px-3 py-1.5 rounded-xl text-xs font-semibold border-2 transition-all"
                  style={{background:sel===i?"#dcfce7":"transparent",borderColor:sel===i?"#16a34a":"#e5e7eb",color:sel===i?"#16a34a":"#6b7280"}}>
                  {t.label}
                </button>
              ))}
            </div>
            {sel===4?(
              <textarea className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:border-green-400" rows={4}
                placeholder="Type your custom message..." value={custom} onChange={e=>setCustom(e.target.value)}/>
            ):(
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-gray-700">{templates[sel].msg}</div>
            )}
            <button onClick={send} className="w-full mt-4 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2" style={{background:"#16a34a"}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Open in WhatsApp
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export const ClientPayments=({client,theme,userId,currency="₹"})=>{
  const [rows,setRows]=useState(null);
  const {isDark,tc,mc,bc}=themeTokens(theme);
  useEffect(()=>{
    if(!userId||!client?.id){setRows([]);return;}
    let cancelled=false;
    setRows(null);
    getDocs(query(collection(db,"users",userId,"payments"),where("clientId","==",client.id)))
      .then(s=>{if(!cancelled)setRows(s.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(b.date||"").localeCompare(a.date||"")));})
      .catch(()=>{if(!cancelled)setRows([]);});
    return()=>{cancelled=true;};
  },[userId,client?.id]);
  const kindLabel=(k)=>({renewal:"Renewal",extension:"Extension",shri:"Via Shri"}[k]||"Payment");
  const total=(rows||[]).reduce((s,p)=>s+(Number(p.amount)||0),0);
  return(
    <div className="rounded-xl border p-4 space-y-2" style={{borderColor:bc,background:isDark?"#0f172a":"#f9fafb"}}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider" style={{color:theme?.primary}}>Payments</p>
        {rows?.length>0&&<p className="text-xs font-bold" style={{color:tc}}>{fmt(total,currency)} total</p>}
      </div>
      {rows===null?<p className="text-xs text-center py-3" style={{color:mc}}>Loading…</p>
      :rows.length===0?<p className="text-xs text-center py-3" style={{color:mc}}>No payments recorded</p>
      :rows.map(p=>(
        <div key={p.id} className="flex items-start justify-between py-2 border-b last:border-0" style={{borderColor:bc}}>
          <div className="min-w-0">
            <p className="text-xs mt-0.5" style={{color:mc}}>{p.date||"—"} · {kindLabel(p.kind)}{p.mode?` · ${p.mode}`:""}</p>
            {p.note&&<p className="text-xs italic mt-0.5 truncate" style={{color:mc}}>{p.note}</p>}
          </div>
          <p className="text-xs font-bold flex-shrink-0 ml-3" style={{color:tc}}>{fmt(Number(p.amount)||0,currency)}</p>
        </div>
      ))}
    </div>
  );
};

export const ClientNotes=({client,theme,userId})=>{
  const toast=useToast();
  const [manualNotes,setManualNotes]=useState([]);
  const [attendanceNotes,setAttendanceNotes]=useState([]);
  const [text,setText]=useState("");
  const [loading,setLoading]=useState(true);
  const {isDark,tc,mc,bc}=themeTokens(theme);
  const cb=isDark?"#1e293b":"#fff";
  const p=theme?.primary||"#0f766e";

  useEffect(()=>{
    if(!userId||!client?.id)return;
    let cancelled=false;
    setLoading(true);setManualNotes([]);setAttendanceNotes([]); // don't show the previous client's notes
    // Boilerplate attendance "notes" (auto-set by the mark paths) aren't real notes.
    const BOILERPLATE=new Set(["Bulk attendance","Scheduled class","Marked from schedule"]);
    Promise.allSettled([
      getDocs(collection(db,"users",userId,"clients",client.id,"notes")),
      getDocs(query(collection(db,"users",userId,"attendance"),where("clientId","==",client.id))),
    ]).then(([n,a])=>{
      if(cancelled)return;
      if(n.status==="fulfilled")
        setManualNotes(n.value.docs.map(d=>({id:d.id,...d.data()})).sort((x,y)=>y.createdAt?.localeCompare(x.createdAt)));
      if(a.status==="fulfilled")
        setAttendanceNotes(a.value.docs.map(d=>({id:d.id,...d.data()}))
          .filter(r=>r.note&&!BOILERPLATE.has(r.note)&&!r.note.startsWith("Batch: ")));
      setLoading(false);
    });
    return()=>{cancelled=true;};
  },[userId,client?.id]);

  const add=async()=>{
    if(!text.trim())return;
    const note={id:uid(),text:text.trim(),createdAt:new Date().toISOString(),type:"manual"};
    setManualNotes(prev=>[note,...prev]);setText("");
    try{await setDoc(doc(db,"users",userId,"clients",client.id,"notes",note.id),{text:note.text,createdAt:note.createdAt,type:"manual"});}
    catch(e){console.error("Note save failed:",e);toast("Couldn't save note — check your connection and try again.","error");}
  };

  const del=async(nid)=>{
    setManualNotes(prev=>prev.filter(n=>n.id!==nid));
    await deleteDoc(doc(db,"users",userId,"clients",client.id,"notes",nid));
  };

  // Combine all notes: manual + attendance (already filtered in the effect)
  const allNotes=[
    ...manualNotes.map(n=>({...n,source:"note"})),
    ...attendanceNotes.map(a=>({
      id:a.id,text:a.note,createdAt:a.date+"T00:00:00",source:"attendance",date:a.date
    })),
  ].sort((a,b)=>b.createdAt?.localeCompare(a.createdAt));

  // Group by Year → Month
  const grouped={};
  allNotes.forEach(n=>{
    const d=new Date(n.createdAt);
    const year=d.getFullYear();
    const month=d.toLocaleString("en-IN",{month:"long"});
    const key=`${year}-${String(d.getMonth()+1).padStart(2,"0")}`;
    const label=`${month} ${year}`;
    if(!grouped[key])grouped[key]={label,notes:[]};
    grouped[key].notes.push(n);
  });
  const groupKeys=Object.keys(grouped).sort((a,b)=>b.localeCompare(a));

  const sourceIcon=(source)=>{
    if(source==="attendance")return"✅";
    if(source==="payment")return"💰";
    return"📝";
  };
  const sourceLabel=(source)=>{
    if(source==="attendance")return"Attendance note";
    if(source==="payment")return"Payment note";
    return"Note";
  };

  return(
    <div className="space-y-3">
      <div className="flex gap-2">
        <textarea className="flex-1 border rounded-xl px-4 py-2.5 text-sm resize-none focus:outline-none"
          style={{background:cb,borderColor:bc,color:tc}} rows={2}
          placeholder="Add a note about this client..." value={text} onChange={e=>setText(e.target.value)}/>
        <button onClick={add} className="px-4 rounded-xl text-sm font-semibold text-white self-stretch" style={{background:p}}>Add</button>
      </div>
      {loading?<p className="text-sm text-center py-4" style={{color:mc}}>Loading...</p>
      :allNotes.length===0?<p className="text-sm text-center py-4" style={{color:mc}}>No notes yet</p>
      :groupKeys.map(key=>(
        <div key={key}>
          <p className="text-xs font-bold uppercase tracking-wider mb-2 mt-3" style={{color:p}}>{grouped[key].label}</p>
          {grouped[key].notes.map(n=>(
            <div key={n.id} className="rounded-xl p-3 border mb-2" style={{background:cb,borderColor:bc}}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-xs font-semibold mb-1" style={{color:mc}}>{sourceIcon(n.source)} {sourceLabel(n.source)}</p>
                  <p className="text-sm" style={{color:tc}}>{n.text}</p>
                </div>
                {n.source==="note"&&(
                  <button onClick={()=>del(n.id)} className="text-red-400 flex-shrink-0">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                  </button>
                )}
              </div>
              <p className="text-xs mt-1.5" style={{color:mc}}>{new Date(n.createdAt).toLocaleString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export const ClientDetailModal=({c,onClose,theme,currency="₹",userId,onRenew,onExtend,bare=false,profile={}})=>{
  const [tab,setTab]=useState("details");
  const {isDark,tc,mc,bc}=themeTokens(theme);
  const {balance:due,expiryDate:expiry}=packageDisplayState(c);

  const Row=({label,value})=>value?(
    <div className="flex items-start justify-between py-2 border-b last:border-0" style={{borderColor:bc}}>
      <span className="text-xs font-semibold w-32 flex-shrink-0" style={{color:mc}}>{label}</span>
      <span className="text-sm text-right flex-1" style={{color:tc}}>{value}</span>
    </div>
  ):null;

  const inner=(
    <div>
      {c.previousClientId&&(
        <div className="mb-3 text-xs px-3 py-2 rounded-xl flex items-start gap-2" style={{background:isDark?"#134e4a":"#f0fdf4",color:theme?.primary||"#0f766e"}}>
          <span>↩</span><span>Returning client — the package &amp; payment history below predates the current package.</span>
        </div>
      )}
      {/* WhatsApp button at top */}
      {c.mobile&&(
        <div className="mb-4">
          <WhatsAppButton client={{...c,phone:c.mobile}} theme={theme} profile={profile}/>
        </div>
      )}
      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl mb-4" style={{background:isDark?"#1e293b":"#f3f4f6"}}>
        {[{id:"details",label:"Details"},{id:"history",label:"History & Notes"}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            className="flex-1 py-2 rounded-xl text-xs font-semibold transition-all"
            style={{background:tab===t.id?(isDark?"#0f172a":"#fff"):"transparent",color:tab===t.id?(theme?.primary||"#0f766e"):(isDark?"#94a3b8":"#9ca3af"),boxShadow:tab===t.id?"0 1px 3px rgba(0,0,0,0.1)":"none"}}>
            {t.label}
          </button>
        ))}
      </div>

      {tab==="details"&&(
      <div className="space-y-4">
        <div className="rounded-xl border p-4 space-y-1" style={{borderColor:bc,background:isDark?"#0f172a":"#f9fafb"}}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{color:theme?.primary}}>Personal</p>
          <Row label="Mobile" value={c.mobile}/>
          <Row label="Email" value={c.email}/>
          <Row label="Date of Birth" value={c.dob}/>
          <Row label="Joining Date" value={c.joiningDate}/>
          <Row label="Location" value={[c.city,c.country].filter(Boolean).join(", ")}/>
          <Row label="Profession" value={c.profession}/>
          <Row label="Sector" value={c.sector}/>
        </div>
        <div className="rounded-xl border p-4 space-y-1" style={{borderColor:bc,background:isDark?"#0f172a":"#f9fafb"}}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{color:theme?.primary}}>Service</p>
          <Row label="Service" value={c.servicesAvailed?.join(", ")||c.serviceAvailed}/>
          <Row label="Delivery" value={c.deliveryType}/>
        </div>
        <div className="rounded-xl border p-4 space-y-1" style={{borderColor:bc,background:isDark?"#0f172a":"#f9fafb"}}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{color:theme?.primary}}>Package</p>
          <Row label="Package" value={c.package?.type&&c.package.type!=="none"?pkgDisplayName(c.package):null}/>
          {(c.package?.type==="custom"||c.package?.type==="template_sessions")&&(
            <><Row label="Total Sessions" value={c.package?.customClasses||c.package?.templateSessions}/><Row label="Remaining" value={`${c.classesRemaining??0} ${sl(profile,2).toLowerCase()}`}/></>
          )}
          <Row label="Start Date" value={c.package?.startDate}/>
          <Row label="Valid Till" value={expiry}/>
          <Row label="Total Fee" value={c.packagePrice?fmt(c.packagePrice,currency):null}/>
          <Row label="Paid" value={c.paid?fmt(c.paid,currency):null}/>
          <div className="flex items-center justify-between py-2">
            <span className="text-xs font-semibold w-32" style={{color:mc}}>Balance</span>
            <span className="text-sm font-bold" style={{color:due>0?"#dc2626":due<0?"#1d4ed8":"#16a34a"}}>
              {due>0?`${fmt(due,currency)} due`:due<0?`${fmt(Math.abs(due),currency)} advance`:"Fully paid ✓"}
            </span>
          </div>
          {onRenew&&c.package?.type&&c.package.type!=="none"&&(
            <div className="flex gap-2 mt-2">
              <button onClick={onRenew}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                style={{background:theme?.primary||"#0f766e",color:"#fff"}}>
                🔄 Renew Package
              </button>
              {["monthly","quarterly","half_yearly","yearly","template_duration"].includes(c.package?.type)&&onExtend&&(
                <button onClick={onExtend}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 border"
                  style={{borderColor:theme?.primary||"#0f766e",color:theme?.primary||"#0f766e",background:"transparent"}}>
                  ➕ Extend
                </button>
              )}
            </div>
          )}
        </div>
        {c.schedule?.length>0&&(
          <div className="rounded-xl border p-4" style={{borderColor:bc,background:isDark?"#0f172a":"#f9fafb"}}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{color:theme?.primary}}>Schedule</p>
            {c.schedule.map((r,i)=>(
              <p key={i} className="text-xs py-1 border-b last:border-0" style={{color:tc,borderColor:bc}}>
                {r.days?.join(", ")} at {r.time} · {r.repeat==="every_2_weeks"?"Every 2 weeks":r.repeat==="every_month"?"Monthly":"Every week"}
              </p>
            ))}
          </div>
        )}
      </div>
      )}

      {tab==="history"&&(
        <div className="space-y-4">
          {/* Package History */}
          <div className="rounded-xl border p-4 space-y-2" style={{borderColor:bc,background:isDark?"#0f172a":"#f9fafb"}}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{color:theme?.primary}}>Package History</p>
            {(c.packageHistory?.length>0)?[...c.packageHistory].reverse().map((pkg,i)=>(
              <div key={pkg.packageId||i} className="flex items-start justify-between py-2 border-b last:border-0" style={{borderColor:bc}}>
                <div>
                  <p className="text-xs font-semibold" style={{color:tc}}>
                    {pkgDisplayName(pkg)}
                    {pkg.status==="active"&&<span className="ml-2 px-1.5 py-0.5 rounded-full text-xs" style={{background:theme?.light,color:theme?.primary}}>Active</span>}
                    {pkg.status==="expired"&&<span className="ml-2 px-1.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">Expired</span>}
                  </p>
                  {pkg.startDate&&<p className="text-xs mt-0.5" style={{color:mc}}>Started: {pkg.startDate}</p>}
                </div>
                <div className="text-right">
                  {pkg.totalAmount>0&&<p className="text-xs font-bold" style={{color:tc}}>{fmt(pkg.totalAmount,currency)}</p>}
                  {(pkg.type==="custom"||pkg.type==="template_sessions")&&(pkg.customClasses||pkg.templateSessions)&&(
                    <p className="text-xs" style={{color:mc}}>
                      {pkg.status==="active"
                        ?`${c.classesAttended||0}/${pkg.customClasses||pkg.templateSessions} ${sl(profile,2).toLowerCase()}`
                        :`${pkg.customClasses||pkg.templateSessions} ${sl(profile,2).toLowerCase()}`}
                    </p>
                  )}
                </div>
              </div>
            )):(
              <p className="text-xs text-center py-3" style={{color:mc}}>No package history yet</p>
            )}
          </div>
          {/* Payments */}
          {userId&&<ClientPayments client={c} theme={theme} userId={userId} currency={currency}/>}
          {/* Notes */}
          {userId&&<ClientNotes client={c} theme={theme} userId={userId}/>}
        </div>
      )}
    </div>
  );
  if(bare)return inner;
  return <Modal title={c.name} onClose={onClose} theme={theme}>{inner}</Modal>;
};

export const ClientFormModal=({f,setF,onSave,onCancel,onMarkInactive,title,clients,services,defSvc,mc,theme,currency="₹",errors={},profile={}})=>{
  useFormGuard(); // pause background sync while this form is open
  const isDark=theme?.dark;
  const mc2=isDark?"#94a3b8":"#6b7280";
  const _unused_CS=sortOtherLast(["Health & Fitness","Education & Tutoring","Arts & Music","Sports & Athletics","Wellness & Therapy","Dance & Performing Arts","Nutrition & Diet","Other"]);
  return(
  <Modal title={title} onClose={onCancel} theme={theme}>
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2"><Inp label="Full Name *" value={f.name} onChange={e=>setF(p=>({...p,name:e.target.value}))} theme={theme} error={errors.name}/></div>
      <div className="col-span-2"><PhoneField label="Mobile" value={f.mobile||""} onChange={v=>setF(p=>({...p,mobile:v}))} onCountryChange={c=>setF(p=>({...p,country:c,city:""}))} error={errors.mobile} theme={theme}/></div>
      <div className="col-span-2"><Inp label="Email" type="email" value={f.email||""} onChange={e=>setF(p=>({...p,email:e.target.value}))} theme={theme} error={errors.email}/></div>
      <Inp label="Date of Birth" type="date" value={f.dob||""} onChange={e=>setF(p=>({...p,dob:e.target.value}))} theme={theme}/>
      <SD label="Profession" value={f.profession} onChange={v=>setF(p=>({...p,profession:v,professionOther:v!=="Other"?"":p.professionOther}))} options={CLIENT_PROFESSIONS} theme={theme}/>
      <SD label="Sector" value={f.sector} onChange={v=>setF(p=>({...p,sector:v,sectorOther:v!=="Other"?"":p.sectorOther}))} options={CLIENT_SECTORS} theme={theme}/>
      {f.profession==="Other"&&<div className="col-span-2"><Inp label="Specify Profession" value={f.professionOther||""} onChange={e=>setF(p=>({...p,professionOther:e.target.value}))} theme={theme} placeholder="e.g. Pole Dancer, Esports Coach..."/></div>}
      {f.sector==="Other"&&<div className="col-span-2"><Inp label="Specify Sector" value={f.sectorOther||""} onChange={e=>setF(p=>({...p,sectorOther:e.target.value}))} theme={theme} placeholder="e.g. Esports, Circus Arts..."/></div>}
      <SD label="Country" value={f.country} onChange={v=>setF(p=>({...p,country:v,city:""}))} options={COUNTRIES} theme={theme}/>
      <SD label="City" value={f.city} onChange={v=>setF(p=>({...p,city:v}))} options={f.country?(CC[f.country]||[]):[]} placeholder={f.country?"Select city":"Country first"} theme={theme}/>
      <div className="col-span-2"><Inp label="Joining Date" type="date" value={f.joiningDate||""} onChange={e=>setF(p=>({...p,joiningDate:e.target.value}))} theme={theme}/></div>
      <div className="col-span-2">
        {services.length>1
          ?<SD label="Service Availed" value={f.serviceAvailed||defSvc} onChange={v=>setF(p=>({...p,serviceAvailed:v}))} options={services} theme={theme}/>
          :<Inp label="Service Availed" value={f.serviceAvailed||defSvc} onChange={e=>setF(p=>({...p,serviceAvailed:e.target.value}))} theme={theme}/>
        }
      </div>
      <div className="col-span-2">
        <SD label="Type of Delivery" value={f.deliveryType||"Studio"} onChange={v=>setF(p=>({...p,deliveryType:v}))} options={["Home Visit","Studio","Online"]} theme={theme}/>
      </div>
      <div className="col-span-2">
        <label className="text-xs font-semibold uppercase tracking-wider block mb-1" style={{color:mc}}>Package</label>
        <PkgBuilder pkg={f.package||{type:"none",totalAmount:0}} onChange={pkg=>setF(p=>({...p,package:pkg}))} theme={theme} currency={currency} profile={profile}/>
        {errors?.packageStart&&<p className="text-xs text-red-500 mt-1">{errors.packageStart}</p>}
      </div>
      <div className="col-span-2">
        <SchedBuilder schedule={f.schedule||[]} onChange={s=>setF(p=>({...p,schedule:s}))} clients={clients} currentClientId={f.id} theme={theme}/>
      </div>
    </div>
    <div className="flex flex-wrap justify-end gap-3 mt-6">
      <Btn variant="secondary" onClick={onCancel} theme={theme}>Cancel</Btn>
      {f.id&&onMarkInactive&&(
        <ConfirmButton
          label="Mark Inactive"
          confirmLabel="Move to Leads?"
          onConfirm={()=>{onMarkInactive();onCancel();}}
          danger
          className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
          style={{background:"#ef4444"}}
        />
      )}
      {f.id
        ?<ConfirmButton
            label={<><Icon name="check" size={16}/>Update</>}
            confirmLabel="Save changes?"
            onConfirm={onSave}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white flex items-center gap-2"
            style={{background:theme?.primary||"#0f766e"}}
          />
        :<Btn onClick={onSave} theme={theme}><Icon name="check" size={16}/>Save Client</Btn>
      }
    </div>
  </Modal>
  );
};
